/**
 * Arabic to English License Plate Character Converter
 * Based on Saudi Arabia license plate character mapping
 */

// Arabic to English character mapping for license plates
// Based on the official Saudi Arabia license plate character mapping
const ARABIC_TO_ENGLISH_MAP = {
  'ا': 'A',  // Alif (U+0627)
  'ب': 'B',  // Ba  
  'ح': 'J',  // Ha (this maps to J, not Jim)
  'د': 'D',  // Dal
  'ر': 'R',  // Ra
  'س': 'S',  // Sin
  'ص': 'X',  // Sad
  'ط': 'T',  // Ta
  'ع': 'E',  // Ain
  'ق': 'G',  // Qaf
  'ك': 'K',  // Kaf
  'ل': 'L',  // Lam
  'م': 'Z',  // Mim
  'ن': 'N',  // Nun
  'ه': 'H',  // Ha (U+0647)
  'و': 'U',  // Waw
  'ي': 'V',  // Ya
  
  // Handle tatweel character (Arabic elongation mark)
  'ـ': '',   // Tatweel - remove it
  
  // Non-standard characters that appear in fuel data (for compatibility)
  'ز': 'Z',  // Zay (appears in fuel data but not official)
  'خ': 'X'   // Kha (appears in fuel data but not official)
};

// English to Arabic character mapping (prioritizing official characters)
const ENGLISH_TO_ARABIC_MAP = {
  'A': 'ا',  // Alif
  'B': 'ب',  // Ba  
  'J': 'ح',  // Ha (official mapping)
  'D': 'د',  // Dal
  'R': 'ر',  // Ra
  'S': 'س',  // Sin
  'X': 'ص',  // Sad (official - prioritized over خ)
  'T': 'ط',  // Ta
  'E': 'ع',  // Ain
  'G': 'ق',  // Qaf
  'K': 'ك',  // Kaf
  'L': 'ل',  // Lam
  'Z': 'م',  // Mim (official - prioritized over ز)
  'N': 'ن',  // Nun
  'H': 'ه',  // Ha
  'U': 'و',  // Waw
  'V': 'ي'   // Ya
};

/**
 * Convert Arabic license plate characters to English
 * @param {string} text - Text containing Arabic characters
 * @returns {string} Text with Arabic characters converted to English
 */
export function convertArabicToEnglish(text) {
  if (!text) return '';
  
  return text
    .toString()
    .split('')
    .map(char => ARABIC_TO_ENGLISH_MAP[char] || char)
    .join('')
    .replace(/ـ/g, ''); // Remove any remaining tatweel characters
}

/**
 * Convert English license plate characters to Arabic
 * @param {string} text - Text containing English characters
 * @returns {string} Text with English characters converted to Arabic
 */
export function convertEnglishToArabic(text) {
  if (!text) return '';
  
  return text
    .toString()
    .split('')
    .map(char => ENGLISH_TO_ARABIC_MAP[char] || char)
    .join('');
}

/**
 * Normalize license plate for comparison by:
 * 1. Converting Arabic to English
 * 2. Removing spaces and special characters
 * 3. Converting to uppercase
 * @param {string} plate - License plate string
 * @returns {string} Normalized plate
 */
export function normalizeLicensePlate(plate) {
  if (!plate) return '';
  
  // First convert Arabic to English, then clean up
  const converted = convertArabicToEnglish(plate.toString());
  
  return converted
    .replace(/ـ/g, '') // Remove Arabic tatweel characters
    .replace(/[^A-Z0-9]/gi, '') // Keep only alphanumeric chars
    .replace(/\s+/g, '') // Remove spaces
    .toUpperCase();
}

/**
 * Generate all possible variations of a license plate for fuzzy matching
 * @param {string} plate - Original license plate
 * @returns {string[]} Array of plate variations
 */
export function generatePlateVariations(plate) {
  if (!plate) return [];
  
  const normalized = normalizeLicensePlate(plate);
  const variations = new Set();
  
  // Add original normalized version
  variations.add(normalized);
  
  // Add version with Arabic converted to English
  variations.add(convertArabicToEnglish(normalized));
  
  // Add version with English converted to Arabic
  variations.add(convertEnglishToArabic(normalized));
  
  // Add version without spaces/dashes
  variations.add(plate.toString().replace(/[-\s]/g, '').toUpperCase());
  
  // Add version with common separators removed
  variations.add(plate.toString().replace(/[-\s._]/g, '').toUpperCase());
  
  return Array.from(variations).filter(v => v.length > 0);
}

/**
 * Check if two license plates match using fuzzy logic
 * @param {string} plate1 - First license plate
 * @param {string} plate2 - Second license plate
 * @returns {boolean} True if plates match
 */
export function licensePlatesMatch(plate1, plate2) {
  if (!plate1 || !plate2) return false;
  
  const variations1 = generatePlateVariations(plate1);
  const variations2 = generatePlateVariations(plate2);
  
  // Check if any variation of plate1 matches any variation of plate2
  for (const v1 of variations1) {
    for (const v2 of variations2) {
      if (v1 === v2) return true;
    }
  }
  
  return false;
}

/**
 * Find the best matching license plate from a list
 * @param {string} targetPlate - Plate to match against
 * @param {string[]} candidatePlates - List of candidate plates
 * @returns {string|null} Best matching plate or null if no match
 */
export function findBestPlateMatch(targetPlate, candidatePlates) {
  if (!targetPlate || !candidatePlates || candidatePlates.length === 0) {
    return null;
  }
  
  const targetVariations = generatePlateVariations(targetPlate);
  
  // First try exact matches
  for (const candidate of candidatePlates) {
    const candidateVariations = generatePlateVariations(candidate);
    
    for (const targetVar of targetVariations) {
      for (const candidateVar of candidateVariations) {
        if (targetVar === candidateVar) {
          return candidate;
        }
      }
    }
  }
  
  // If no exact match, try similarity matching
  let bestMatch = null;
  let bestScore = 0;
  
  for (const candidate of candidatePlates) {
    const score = calculatePlateSimilarity(targetPlate, candidate);
    if (score > bestScore && score > 0.7) { // 70% similarity threshold
      bestScore = score;
      bestMatch = candidate;
    }
  }
  
  return bestMatch;
}

/**
 * Calculate similarity score between two license plates
 * @param {string} plate1 - First plate
 * @param {string} plate2 - Second plate
 * @returns {number} Similarity score between 0 and 1
 */
function calculatePlateSimilarity(plate1, plate2) {
  const norm1 = normalizeLicensePlate(plate1);
  const norm2 = normalizeLicensePlate(plate2);
  
  if (norm1 === norm2) return 1.0;
  
  // Calculate Levenshtein distance
  const distance = levenshteinDistance(norm1, norm2);
  const maxLength = Math.max(norm1.length, norm2.length);
  
  if (maxLength === 0) return 0;
  
  return 1 - (distance / maxLength);
}

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

export default {
  convertArabicToEnglish,
  convertEnglishToArabic,
  normalizeLicensePlate,
  generatePlateVariations,
  licensePlatesMatch,
  findBestPlateMatch
};